export type AccessDecision =
  | "missing_email"
  | "denied"
  | "invited"
  | "bootstrap_admin"

export type AppEnv = {
  PUBLIC_CLERK_PUBLISHABLE_KEY?: string
  PUBLIC_BOOTSTRAP_ADMIN_EMAILS?: string
  PUBLIC_INVITED_EMAILS?: string
}

export type AuthConfig = {
  clerkPublishableKey: string | null
  bootstrapAdminEmails: string[]
  invitedEmails: string[]
}

function parseEmailList(raw: string | undefined): string[] {
  if (!raw) {
    return []
  }

  return raw
    .split(",")
    .map((entry) => entry.trim().toLowerCase())
    .filter(
      (entry, index, all) => entry.length > 0 && all.indexOf(entry) === index,
    )
}

function getRuntimeEnv(): AppEnv {
  return {
    PUBLIC_CLERK_PUBLISHABLE_KEY: import.meta.env.PUBLIC_CLERK_PUBLISHABLE_KEY,
    PUBLIC_BOOTSTRAP_ADMIN_EMAILS: import.meta.env
      .PUBLIC_BOOTSTRAP_ADMIN_EMAILS,
    PUBLIC_INVITED_EMAILS: import.meta.env.PUBLIC_INVITED_EMAILS,
  }
}

export function getAuthConfig(env: AppEnv = getRuntimeEnv()): AuthConfig {
  return {
    clerkPublishableKey: env.PUBLIC_CLERK_PUBLISHABLE_KEY || null,
    bootstrapAdminEmails: parseEmailList(env.PUBLIC_BOOTSTRAP_ADMIN_EMAILS),
    invitedEmails: parseEmailList(env.PUBLIC_INVITED_EMAILS),
  }
}

export function getAccessDecision(
  email: string | null | undefined,
  config: Pick<AuthConfig, "bootstrapAdminEmails" | "invitedEmails">,
): AccessDecision {
  if (!email) {
    return "missing_email"
  }

  const normalizedEmail = email.trim().toLowerCase()

  if (config.bootstrapAdminEmails.includes(normalizedEmail)) {
    return "bootstrap_admin"
  }

  if (config.invitedEmails.includes(normalizedEmail)) {
    return "invited"
  }

  return "denied"
}

function getClerkInvitationAccess(
  publicMetadata: unknown,
): AccessDecision | null {
  if (!publicMetadata || typeof publicMetadata !== "object") {
    return null
  }

  const metadata = publicMetadata as Record<string, unknown>
  return metadata.craftForceAccess === "invited" ? "invited" : null
}

export function getEffectiveAccessDecision(
  email: string | null | undefined,
  config: Pick<AuthConfig, "bootstrapAdminEmails" | "invitedEmails">,
  publicMetadata?: unknown,
): AccessDecision {
  const envAccess = getAccessDecision(email, config)
  return envAccess === "denied"
    ? (getClerkInvitationAccess(publicMetadata) ?? "denied")
    : envAccess
}
